import React from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../utils/mq';
import { MaterialSymbol } from '../atoms/material-symbol';
import { Cta } from './cta';
import { useImageComponent, useLinkComponent } from '../utils/component';

export interface SponsoredComponentProps {
  tops: [
    {
      iconSlug: string;
      title: string;

      description: string;
    },
  ];
  title: string;
  description: string;
  imageId: string;
  sticker: string;
  url: string;
  onClick?: () => void;
  buttonLabel?: string;
  hiddenOnMobile?: boolean;
}

const SponsoredContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: flex-start;
  padding: 15px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  margin: 0 0 var(--spacer-fluid);
  position: relative;

  &.no-mobile {
    ${mq.lte(
      DEVICE_SIZE.LARGE,
      css`
        display: none;
      `,
    )}
  }
`;

const Badge = styled.div`
  background: #e4002a;
  color: white;
  font-size: 16px;
  position: absolute;
  font-weight: bold;
  top: 0;
  left: 0;
  padding: 4px 15px;
  border-radius: 6px 0 6px 0;
  margin: 0;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  padding: 15px;
  margin-bottom: 0;
  margin-top: 30px;
  border: 1px solid;
  border-color: #edf0f4;
  border-radius: 6px;
`;

const ImageContainer = styled.div`
  max-width: 100%;
  height: auto;
`;

const Title = styled.div`
  font-size: 18px;
  text-align: center;
  font-weight: bold;
`;

const Description = styled.div`
  text-align: center;
  font-weight: bold;
`;

const Tops = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  padding-left: 32px;
  width: 100%;
  list-style-type: none;
`;

const Top = styled.li`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  gap: 10px;
`;

const TopLabel = styled.span`
  font-size: 14px;
  text-align: left;
  font-weight: initial;
  font-style: italic;
  text-decoration: none;
`;

const CtaContainer = styled.div`
  width: 100%;
  font-family: 'Bitter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, serif;
  font-size: 18px;
`;

export const SponsoredComponent = (props: SponsoredComponentProps) => {
  const { tops, onClick, sticker, title, imageId, description, buttonLabel, url, hiddenOnMobile } = props;
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  return (
    <SponsoredContainer className={hiddenOnMobile ? 'no-mobile' : ''}>
      <Badge>
        <LinkComponent href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
          {sticker}
        </LinkComponent>
      </Badge>
      <MainContainer>
        <ImageContainer>
          <LinkComponent href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
            <ImageComponent
              imageId={imageId}
              alt={sticker}
              desktopHeight={200}
              desktopWidth={240}
              mobileWidth={240}
              mobileHeight={200}
            />
          </LinkComponent>
        </ImageContainer>
        <Title>
          <LinkComponent href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
            {title}
          </LinkComponent>
        </Title>
        <Description>
          <LinkComponent href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
            {description}
          </LinkComponent>
        </Description>
        <LinkComponent href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
          {tops && (
            <Tops>
              {tops.map((detail: any, index: number) => {
                return (
                  <Top key={index}>
                    <MaterialSymbol symbol={detail.iconSlug} fill={0} weight={400} size={14} />
                    <TopLabel>{detail.description}</TopLabel>
                  </Top>
                );
              })}
            </Tops>
          )}
        </LinkComponent>
        <CtaContainer>
          <Cta title={sticker} href={url} rel={['nofollow']} onClick={onClick} openInNewWindow>
            {buttonLabel}
          </Cta>
        </CtaContainer>
      </MainContainer>
    </SponsoredContainer>
  );
};

export default SponsoredComponent;
